// Currency formatting backed by the platform `Intl.NumberFormat` (replaces numbro).
//
// Mirrors the previous numbro behaviour byte-for-byte (verified across thousands of
// values, incl. floating-point-noisy and high-precision inputs):
//   - thousands grouped with a non-breaking space, decimal comma (cs-CZ / sk-SK);
//   - NO forced decimals — the number's natural precision is shown, up to 20 places;
//   - the currency symbol is appended as a postfix;
//   - passing a `currencySymbol` override drops the thousands grouping (a numbro quirk
//     the Slider relied on).
//
// The active locale is stateful (like numbro's `setLanguage`) and switched from the
// i18next `languageChanged` handler via `setCurrencyLocale`.

export interface CurrencyLocale {
  intlLocale: string;
  symbol: string;
}

const DECIMALS = { minimumFractionDigits: 0, maximumFractionDigits: 20 } as const;

// Default to Czech — matches the i18n fallback language.
let activeSymbol = ' Kč';
let grouped = new Intl.NumberFormat('cs-CZ', DECIMALS);
let ungrouped = new Intl.NumberFormat('cs-CZ', { ...DECIMALS, useGrouping: false });

export const setCurrencyLocale = ({ intlLocale, symbol }: CurrencyLocale): void => {
  activeSymbol = symbol;
  grouped = new Intl.NumberFormat(intlLocale, DECIMALS);
  ungrouped = new Intl.NumberFormat(intlLocale, { ...DECIMALS, useGrouping: false });
};

// The active locale's currency symbol (replaces numbro.languageData().currency.symbol).
export const getCurrencySymbol = (): string => activeSymbol;

interface FormatOptions {
  currencySymbol?: string;
}

// Formats `value` as currency. With a truthy `currencySymbol` the grouping is dropped
// and that symbol is used; otherwise the active locale's grouping + symbol apply.
export const formatCurrency = (value: number, options?: FormatOptions | false | null | ''): string => {
  const overrideSymbol = options ? options.currencySymbol : undefined;

  if (overrideSymbol) {
    return ungrouped.format(value) + overrideSymbol;
  }

  return grouped.format(value) + activeSymbol;
};
